/**
 * Shared eligibility types — imported by both frontend (src/) and backend (functions/).
 * Single source of truth for AAA rejection, warnings, and rejection taxonomy.
 *
 * Domain types + helpers only — NO UI copy (guidance text lives in src/constants/).
 */

/**
 * Normalized RPA-vendor benefits shape written to the check doc's `benefits`
 * field. Each vendor's parser (see functions/src/eligibilityCheck/rpaVendorsBackend.ts)
 * reduces its bot's raw output to this shape so the slideout and Firestore
 * schema stay vendor-neutral. Named `VspBenefits` for backward compatibility
 * with existing frontend state; treat it as the generic RPA benefits shape.
 */
export interface VspBenefits {
  routineExamCopay: number | null;
  medicalExamCopay: number | null;
  genCopay: number | null;
  materialCopay: number | null;
  maxFrameAllowance: number | null;
  contactLensAllowance: number | null;
  deductible: number | null;
}

// ── Structured VSP benefits (post-2026 bot format) ───────────────────────
// The VSP bot now scrapes the WellVision portal into a structured payload
// rather than the flat dollar-label strings of `VspBenefits`. New checks store
// `VspDetailedBenefits`; legacy checks keep the flat `VspBenefits`. The webhook
// parser and the slideout discriminate on the presence of `vspWellVisionPlan`
// (see `isVspDetailedBenefits`), so both shapes coexist on the `benefits` field.

/**
 * One benefit row in the WellVision plan or Specialty Eyecare section. All
 * fields are free-text as displayed on the VSP portal (e.g. copay "$10",
 * frequency "Every 12 months", coverage "Covered in full").
 */
export interface VspBenefitLine {
  name: string;
  coverage: string;
  copay: string;
  frequency: string;
  description: string;
  /** Optional "extra savings" callout shown on some benefits. */
  extraSavings?: string;
}

/** A lens enhancement covered only with an additional copay (name + cost). */
export interface VspLensEnhancementCopay {
  name: string;
  value: string;
}

/**
 * Prescription Lenses benefit — richer than a plain `VspBenefitLine` because
 * VSP splits lens enhancements into "fully covered" vs "covered with an
 * additional copay".
 */
export interface VspPrescriptionLenses {
  coverage: string;
  copay: string;
  frequency: string;
  fullyCoveredLensEnhancements: string;
  lensEnhancementsCoveredWithAdditionalCopay: VspLensEnhancementCopay[];
}

/** The patient's WellVision plan: standard benefit lines + prescription lenses. */
export interface VspWellVisionPlan {
  benefits: VspBenefitLine[];
  prescriptionLenses: VspPrescriptionLenses;
}

/** Specialty eyecare services through a VSP doctor — optional portal section. */
export interface VspSpecialtyEyecareServices {
  benefits: VspBenefitLine[];
}

/** Structured VSP result — the post-2026 VSP bot webhook shape. */
export interface VspDetailedBenefits {
  vspWellVisionPlan: VspWellVisionPlan;
  specialtyEyecareServices?: VspSpecialtyEyecareServices;
}

/**
 * Discriminate the structured VSP shape from the flat `VspBenefits` (and from
 * `EyemedBenefits`). Shared by the webhook (emptiness check) and the slideout
 * (which renderer to use) so both agree on what "new format" means.
 */
export function isVspDetailedBenefits(
  benefits: unknown,
): benefits is VspDetailedBenefits {
  return (
    typeof benefits === "object" &&
    benefits !== null &&
    "vspWellVisionPlan" in benefits
  );
}

/**
 * Whether the prescription-lenses section carries any displayable data. Shared
 * by the webhook (degraded-data detection) and the slideout (whether to render
 * the lenses card) so both agree on what "blank lenses" means.
 */
export function prescriptionLensesHaveData(
  lenses: VspPrescriptionLenses,
): boolean {
  return (
    lenses.coverage.trim() !== "" ||
    lenses.copay.trim() !== "" ||
    lenses.frequency.trim() !== "" ||
    lenses.fullyCoveredLensEnhancements.trim() !== "" ||
    lenses.lensEnhancementsCoveredWithAdditionalCopay.length > 0
  );
}

/**
 * Whether a benefit line carries any displayable data. An empty portal row
 * parses to all-blank strings; this shared predicate lets the slideout skip
 * rendering such placeholder lines (no "—" rows, and a section whose lines are
 * all blank is hidden) and the webhook drop them at parse time — both agreeing
 * on what "blank line" means.
 */
export function vspBenefitLineHasData(line: VspBenefitLine): boolean {
  return (
    line.name.trim() !== "" ||
    line.coverage.trim() !== "" ||
    line.copay.trim() !== "" ||
    line.frequency.trim() !== "" ||
    line.description.trim() !== "" ||
    (line.extraSavings ?? "").trim() !== ""
  );
}

/**
 * Whether a structured VSP result carries no displayable data anywhere — no
 * WellVision benefit lines, blank prescription lenses, and no specialty
 * section. The webhook uses this as the "bot reported success but scraped
 * nothing → mark error" safety net; the slideout uses the same notion to fall
 * back to an empty-state message instead of an empty panel.
 */
export function vspDetailedBenefitsAreEmpty(
  benefits: VspDetailedBenefits,
): boolean {
  const {benefits: lines, prescriptionLenses} = benefits.vspWellVisionPlan;
  const hasSpecialty =
    (benefits.specialtyEyecareServices?.benefits.length ?? 0) > 0;
  return (
    lines.length === 0 &&
    !prescriptionLensesHaveData(prescriptionLenses) &&
    !hasSpecialty
  );
}

// ── Section/HTML VSP benefits (2026-07 Eyefinity bot format) ─────────────
// The newest VSP bot posts each portal section as a labeled raw-HTML benefits
// table (`Data.sections[]`) instead of scraping individual fields. The webhook
// parser sanitizes the HTML at ingest (shared/benefitHtml.ts) and stores
// label+html pairs; the slideout lists the labels (plan names) and opens each
// table in a popup. Discriminated from the other two VSP shapes by the
// `sections` key (see `isVspSectionsBenefits`), so all three coexist on the
// check doc's `benefits` field.

/** One scraped portal section: the plan name + its sanitized HTML benefits table. */
export interface VspHtmlSection {
  label: string;
  html: string;
}

/** Section/HTML VSP result — the 2026-07 VSP bot webhook shape. */
export interface VspSectionsBenefits {
  sections: VspHtmlSection[];
}

/**
 * Discriminate the sections shape from the flat/structured VSP shapes (and
 * from `EyemedBenefits`, whose category values are objects, never arrays).
 * Shared by the webhook (emptiness check) and the slideout (which renderer to
 * use) so both agree on what "sections format" means.
 */
export function isVspSectionsBenefits(
  benefits: unknown,
): benefits is VspSectionsBenefits {
  return (
    typeof benefits === "object" &&
    benefits !== null &&
    Array.isArray((benefits as {sections?: unknown}).sections)
  );
}

/**
 * Whether a sections-shape VSP result carries no displayable data. The parser
 * drops sections without visible table text (a label alone is not benefits
 * data), so "no sections survived" is the webhook's "bot reported success but
 * scraped nothing → mark error" signal; the slideout uses the same notion for
 * its empty state.
 */
export function vspSectionsBenefitsAreEmpty(
  benefits: VspSectionsBenefits,
): boolean {
  return benefits.sections.length === 0;
}

/**
 * Categorized EyeMed benefits — the retired engine's three-category shape,
 * and what every EyeMed check stored before the in-house lane read the
 * benefits page tab by tab (such checks are kept, never migrated):
 * each key is a category (e.g. "Routine", "Additional Purchase"), value is a
 * map of benefit labels to descriptions. Order and group headings are not
 * kept (Firestore stores map keys sorted). Still written by the bot; rendered
 * by the panel's legacy branch; never migrated.
 */
export type EyemedBenefits = Record<string, Record<string, string>>;

// ── EyeMed tabs benefits (the in-house lane's capture) ───────────────────
// The in-house EyeMed lane reads the member's benefits page tab by tab and
// delivers every tab as the portal lists it: its name, whether it was read
// with rows, read with no rows, or could not be read, and every table it
// shows — the table's header cells, its group headings and its rows in
// printed order. Stored on the check doc's `benefits` field beside the
// legacy category map above and told apart by the `tabs` array
// (`isEyemedTabsBenefits`), the way the VSP shapes are told apart by
// `vspWellVisionPlan` and `sections`. Arrays throughout because Firestore
// sorts map keys: a map keyed by tab name or by row label would lose the
// portal's order, and a label printed twice would have nowhere to go.

/**
 * A tab's read state: `rows` — read, at least one benefit row; `empty` —
 * read, no benefit rows (the portal printed an empty table); `failed` — the
 * tab could not be opened or one of its tables never rendered. A tab the
 * portal did not list has no entry at all.
 */
export type EyemedTabState = "rows" | "empty" | "failed";

export const EYEMED_TAB_STATES: readonly EyemedTabState[] = ["rows", "empty", "failed"];

export function isEyemedTabState(value: unknown): value is EyemedTabState {
  return typeof value === "string" &&
    (EYEMED_TAB_STATES as readonly string[]).includes(value);
}

/** One row as printed: its cells in column order, trimmed. A benefit table's
 *  row is the label and the value beside it; the balances table prints three
 *  columns ("Declining Balance Package", "Starting Balance", "Remaining
 *  Balance"). Every cell is kept, so a reader can rebuild the table as it was. */
export interface EyemedRow {
  cells: string[];
}

/** The rows under one group heading ("Exam Services"); `heading: null`
 *  holds the rows printed before any heading. */
export interface EyemedGroup {
  heading: string | null;
  rows: EyemedRow[];
}

/**
 * Where a table was read: `benefits` — the tab's benefit table in the benefit
 * matrix; `balances` — the balances table the portal shows beside it under
 * some tabs. The capture tags every table it delivers; a table with no tag was
 * stored before tables were tagged and reads as `benefits`. The panel shows
 * the benefit tables; a balances table is stored and not shown.
 */
export type EyemedTableSource = "benefits" | "balances";

export const EYEMED_TABLE_SOURCES: readonly EyemedTableSource[] = ["benefits", "balances"];

export function isEyemedTableSource(value: unknown): value is EyemedTableSource {
  return typeof value === "string" &&
    (EYEMED_TABLE_SOURCES as readonly string[]).includes(value);
}

/** One table a tab shows: where it was read, its column-header row's cells as
 *  printed (empty when the table printed none) and its groups in page order. */
export interface EyemedTable {
  source?: EyemedTableSource;
  header: string[];
  groups: EyemedGroup[];
}

/** One tab of the benefits page, named as printed ("LASIK", not "Lasik").
 *  `tables` is empty unless the state is `rows`. */
export interface EyemedTab {
  name: string;
  state: EyemedTabState;
  tables: EyemedTable[];
}

// ── The blocks beside the tabs ───────────────────────────────────────────
// Three more reads the lane makes on pages the check already visits, each
// delivered as a tagged value: `read` with its values as printed (`null` for
// a field the portal printed empty — "not shown"), or `failed` when the read
// could not be made. Failed never fails the check and never changes what the
// tabs say; the two states are kept apart so a reader can tell "the portal
// printed no group" from "we could not read the member block". Optional on
// the shape, so a payload or stored check from before the blocks is valid as
// it is, and a not-found ending (`{tabs: []}`) carries none.

/** The plan as the search results row the check picked prints it. */
export type EyemedPlanBlock =
  | {state: "read"; name: string | null}
  | {state: "failed"};

/** The member block on the Member Details page: the member ID the portal
 *  prints — whatever ID the check was asked with — and the group. */
export type EyemedMemberBlock =
  | {state: "read"; memberId: string | null; group: string | null}
  | {state: "failed"};

/** One row of the per-service eligibility table, every cell a trimmed string
 *  as printed and a blank cell an empty string: the service ("Exam"), the
 *  literal Yes/No, the "Member Eligible As Of" date and the frequency. */
export interface EyemedEligibilityRow {
  service: string;
  eligible: string;
  eligibleAsOf: string;
  frequency: string;
}

/** One tab of the eligibility answer — "Routine" (the covered services) or
 *  "Additional Purchase" (the same materials as discount purchases) — named
 *  as the portal prints it (empty when the answer was one table with no
 *  tabs), its header cells verbatim (the panel's captions) and its rows in
 *  printed order. */
export interface EyemedEligibilityTab {
  name: string;
  header: string[];
  rows: EyemedEligibilityRow[];
}

/** The per-service eligibility answer the Service Eligibility form renders
 *  on Check Eligibility: every tab of it as a named table, in the portal's
 *  order with the SHOWN tab first — `tabs[0]` is the one table any verdict
 *  reads (the covered services), the rest are shown to staff under it and
 *  never read for meaning. `read` whose first tab has no rows (or no tabs
 *  at all) = the table rendered empty. */
export type EyemedEligibilityBlock =
  | {state: "read"; tabs: EyemedEligibilityTab[]}
  | {state: "failed"};

/** The tabs shape: the portal's tab row, in its order, plus — on a delivery
 *  that read them — the plan, the member block and the eligibility table. */
export interface EyemedTabsBenefits {
  tabs: EyemedTab[];
  plan?: EyemedPlanBlock;
  member?: EyemedMemberBlock;
  eligibility?: EyemedEligibilityBlock;
}

/**
 * Discriminate the tabs shape from the legacy category map and from the VSP
 * shapes. A legacy map's values are objects, never arrays, so a map can carry
 * no `tabs` array; `sections` and `vspWellVisionPlan` name the VSP shapes.
 * Shared by the ingest (emptiness), the panel (which renderer) and the
 * rollout script (which checks predate the shape), so all agree.
 */
export function isEyemedTabsBenefits(
  benefits: unknown,
): benefits is EyemedTabsBenefits {
  return (
    typeof benefits === "object" &&
    benefits !== null &&
    Array.isArray((benefits as {tabs?: unknown}).tabs)
  );
}

/**
 * Whether a tabs-shape result carries no usable data: the page listed no
 * tabs, or every listed tab was read with no rows. A `failed` tab is content
 * — the member was found and the tab exists, it just could not be read — so
 * a result with any failed tab is NOT empty. Shared by the ingest (success vs
 * the member-not-found update) and the panel, so the two can never disagree.
 */
export function eyemedTabsBenefitsAreEmpty(benefits: EyemedTabsBenefits): boolean {
  return benefits.tabs.length === 0 || benefits.tabs.every((t) => t.state === "empty");
}

/**
 * Whether the eligibility table lists at least one service and reads No for
 * every one of them — the one verdict computed from the blocks: the patient
 * has nothing left this period, whatever the visit is for, so the check is
 * not eligible. Decided once at ingest (stamped `eyemedAllServicesNo` on the
 * doc) and read everywhere through the terminal classifier; the panel
 * computes nothing. One No among Yeses, a table read with no rows, a failed
 * block or no block at all is false — the check is eligible as before. Reads
 * the SHOWN tab's rows only (`tabs[0]`): the other tabs (Additional
 * Purchase, the materials as discount purchases, every one Yes) are shown to
 * staff and never take part — they would turn every used-up plan eligible.
 */
export function eyemedAllServicesNo(benefits: EyemedTabsBenefits): boolean {
  const block = benefits.eligibility;
  if (!block || block.state !== "read") return false;
  const shown = block.tabs[0];
  if (!shown || shown.rows.length === 0) return false;
  return shown.rows.every((row) => row.eligible.trim().toLowerCase() === "no");
}

/** The benefit rows a tab carries across its tables and groups — the
 *  scraper's `rows` / `empty` decision and the panel's section count. */
export function eyemedTabRowCount(tab: EyemedTab): number {
  return tab.tables.reduce(
    (n, table) => n + table.groups.reduce((m, group) => m + group.rows.length, 0),
    0,
  );
}

/** Structured AAA (Request Validation) segment — no PHI, safe for analytics. */
export interface AAASegment {
  rejectReasonCode?: string;
  rejectReason?: string;
  followUpActionCode?: string;
  followUpAction?: string;
  /** Entity context: "IL"=subscriber, "QC"=dependent, "PR"=payer, "1P"=provider. */
  entityIdentifierCode?: string;
  /** X12 loop location (e.g. "Loop 2100C"). */
  location?: string;
  /** Stedi-provided resolution guidance. */
  possibleResolutions?: string;
}

/** Stedi warning — advisory (not AAA), e.g. member ID format hints. */
export interface StediWarning {
  code?: string;
  description?: string;
}

/** Normalized rejection categories for analytics and UI routing. */
export type RejectionCategory =
  | "member_not_found"
  | "invalid_dob"
  | "coverage_terminated"
  | "no_match_or_invalid_subscriber"
  | "other";

/** Patient data fields that can be corrected via SMS outreach. */
export type PatientField = "dateOfBirth" | "memberId" | "payer" | "insuranceCard";

/** Who needs to act on a failed eligibility check. */
export type RecoveryAction =
  | "patient_correction"
  | "staff_review"
  | "auto_retry"
  | "ops_escalation";

/** Persisted alongside failed eligibility checks for downstream consumers (SMS automation, batch review). */
export interface RecoveryContext {
  recoveryAction: RecoveryAction;
  rejectionCategory?: RejectionCategory;
  /** Fields the patient needs to fix — drives SMS template selection. */
  patientFields?: PatientField[];
  /** Whether patient has a phone number on file. Set by caller, not the router. */
  smsEligible?: boolean;
  retryable: boolean;
  /** Original action before promotion (e.g. auto_retry exhausted → staff_review). */
  promotedFrom?: RecoveryAction;
}

/** Structured rejection reason — present only when status = "lookup_error". */
export interface RejectionInfo {
  category: RejectionCategory;
  /** Clean, human-readable reason (<=120 chars). Absent for category "other". */
  message?: string;
  /** Sanitized description from rejection rows — for analytics, never surfaced in UI. */
  rawNormalizedReason?: string;
}

/** Human-readable label for AAA entity identifier codes. */
export function aaaEntityLabel(code?: string): string {
  switch (code) {
    case "QC": return "Dependent";
    case "IL": return "Subscriber";
    case "1P": return "Provider";
    case "PR": return "Payer";
    default: return code ?? "Unknown";
  }
}

/**
 * Entity priority for sorting AAA segments (lower = more relevant to user).
 * EDI hierarchy: dependent (most specific) → subscriber → provider → payer.
 */
export function aaaEntityPriority(code?: string): number {
  switch (code) {
    case "QC": return 0;  // dependent — most specific failure
    case "IL": return 1;  // subscriber
    case "1P": return 2;  // provider
    case "PR": return 3;  // payer
    default: return 4;
  }
}

/**
 * Select the most relevant AAA segment from a list.
 * Assumes segments are already sorted by entity priority (done server-side).
 * Returns the first segment, or null if empty.
 */
export function selectPrimaryAAA(segments: AAASegment[]): AAASegment | null {
  return segments.length > 0 ? segments[0] : null;
}

/**
 * AAA reject-reason codes that indicate the *provider/submitter* — not the
 * patient — is the problem (e.g. 41: authorization/access restriction, the
 * group NPI isn't enrolled/authorized with the payer; 43/44: invalid provider
 * identification/name). Backend routes these to ops_escalation (never
 * patient_correction or auto_retry); frontend uses them to pick
 * provider-enrollment guidance copy.
 */
export const PROVIDER_ENROLLMENT_AAA_CODES = new Set(["41", "43", "44"]);

export function hasProviderEnrollmentReject(codes?: (string | undefined)[]): boolean {
  return (codes ?? []).some(
    (c) => c !== undefined && PROVIDER_ENROLLMENT_AAA_CODES.has(c),
  );
}

/**
 * AAA reject-reason codes that indicate a TRANSIENT payer-side condition —
 * the request itself is fine, the payer just couldn't answer right now:
 *   42 — Unable to Respond at Current Time (timeout / capacity)
 *   79, 80 — payer system unavailable / throttling
 * Stedi classifies exactly {42,79,80} as retryable (temporary payer issues),
 * resolvable by retrying with backoff for up to ~8h. Backend routes these to
 * auto_retry (never staff_review); see functions/.../eligibilitySweep.ts.
 */
export const TRANSIENT_PAYER_AAA_CODES = new Set(["42", "79", "80"]);

export function hasTransientPayerReject(codes?: (string | undefined)[]): boolean {
  return (codes ?? []).some(
    (c) => c !== undefined && TRANSIENT_PAYER_AAA_CODES.has(c),
  );
}
